import type {Filter} from '@/modules/common/models/filters';
import type {UsePopularFilterItems} from '@/modules/hotels/hooks/usePopularFilters';

import {Chip} from '@travel-frontend/ui-components';
import {without} from 'lodash';
import {useCallback, useRef} from 'react';

type Props = Pick<
  UsePopularFilterItems[number],
  'id' | 'active' | 'label' | 'filter' | 'value' | 'type' | 'disabled'
> & {
  handleChange: (filter: {
    id: Filter['id'];
    value: Filter['value'];
    setValue: Filter['setValue'];
  }) => (event: {
    type: 'clear' | 'pop' | 'set' | 'push';
    value: null | number | string | Array<string>;
  }) => void;
  isSmallView?: boolean;
};

export const Item = ({
  id,
  active,
  label,
  filter,
  value,
  type,
  disabled,
  isSmallView,
  handleChange,
}: Props) => {
  const chipRef = useRef<HTMLDivElement>(null);
  const handleClick = useCallback(() => {
    const handle = handleChange(filter);

    if (active) {
      if (
        type === 'multi' &&
        Array.isArray(filter.value) &&
        filter.value.length > 0
      ) {
        const nextValue = without(filter.value, value);
        if (nextValue.length > 0) {
          handle({type: 'pop', value: nextValue});
        } else {
          handle({
            type: 'clear',
            value: [],
          });
        }
      } else {
        handle({
          type: 'clear',
          value: type === 'multi' ? [] : null,
        });
      }
    } else if (type === 'multi' && Array.isArray(filter.value)) {
      handle({
        type: 'push',
        value: filter.value.concat(value),
      });
    } else {
      let nextValue: string | number | Array<string> = value;
      if (type === 'multi') {
        nextValue = [value];
      } else if (id.includes('category')) {
        nextValue = Number(value);
      }

      handle({
        type: 'set',
        value: nextValue,
      });
    }

    if (chipRef.current) {
      chipRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  }, [active, filter, handleChange, id, type, value]);

  return (
    <Chip
      ref={chipRef}
      disabled={disabled}
      active={active}
      variant={active ? 'filled' : 'outlined'}
      label={label}
      size={isSmallView ? 'small' : 'medium'}
      onClick={handleClick}
    />
  );
};
